import { Edit2, Search, Trash2 } from "lucide-react";
import Image from "next/image";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { FlashingValue } from "src/common/FlashingValue";
import BriefCaseFilledIcon from "src/assets/images/Briefcase-Fiilled.svg";
import Briefcase from "src/assets/images/Briefcase.svg";
import Pagination from "src/common/pagination";
import ConfirmationModal from "src/common/ConfirmationModal";
import { fetchWrapper } from "src/helpers";
import { getSectorColor } from "src/helpers/color";
import {
  formatCurrencyWithLocalString,
  formatPercentage,
} from "src/helpers/numberFormatter";
import { alertService } from "src/services";
import EditPageElement from "src/common/editModal";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { debounce } from "lodash";
import { getFormattedDate } from "src/helpers/dateFormatter";
import { getSortIcon, SortConfig } from "src/helpers/api/sortconfig";
import FreeTrialRestrictionModal from "src/common/freeTrialRestrictionModal";
import WatchlistSkeleton from "src/components/skeleton/WatchlistSkeleton";
import PortfolioCard from "./portfolioCard";

// SortableHeader component moved outside to avoid "component created during render" error
const SortableHeader = ({
  label,
  sortKey,
  className = "",
  sortConfig,
  onSort,
}: {
  label: string;
  sortKey: string;
  className?: string;
  sortConfig: SortConfig;
  onSort: (key: string) => void;
}) => {
  const isCenterAligned =
    sortKey === "symbol" ||
    sortKey === "created_at" ||
    sortKey === "ltp" ||
    sortKey === "change" ||
    sortKey === "status" ||
    sortKey === "is_recurring";

  return (
    <th
      className={`py-3 pl-6 pr-0 text-sm font-medium normal-case text-[#3D3D3D] dark:text-white cursor-pointer select-none group relative ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <div
        className={`flex items-center relative pr-5 ${
          label === "Stock"
            ? "justify-start"
            : isCenterAligned || label === "Overall Gain" || label === "Notes"
              ? "justify-center"
              : "justify-end"
        }`}
      >
        <span
          className={`
              ${label === "Notes" ? "max-w-[250px] truncate" : ""}
            `}
        >
          {label}
        </span>
        {getSortIcon(sortConfig, sortKey)}
      </div>
    </th>
  );
};

export interface WatchlistData {
  id: string;
  record_date: string;
  alert_for: number;
  above_price: number;
  below_price: number;
  symbol: string;
  status: string;
  volatility: string;
  notes: string;
  is_recurring?: boolean;
  ltp: number;
  close: number;
  volume: number;
  lastUpdated: Date;
  sector: string;
  added_to_portfolio: boolean;
}

interface IWatchlistTableProps {
  getData: () => Promise<void>;
  records: WatchlistData[];
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  totalItems: number;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  rowsPerPage: number;
  selectedRange: string;
  setSelectedRange: Dispatch<SetStateAction<string>>;
  params: URLSearchParams;
  setRowsPerPage: Dispatch<SetStateAction<number>>;
  sortConfig: SortConfig;
  handleSort: (key: string) => void;
  isLoading?: boolean;
}

const WatchlistTable = (props: IWatchlistTableProps) => {
  const {
    getData,
    records,
    searchQuery,
    setCurrentPage,
    setSearchQuery,
    currentPage,
    totalItems,
    rowsPerPage,
    selectedRange,
    setSelectedRange,
    params,
    setRowsPerPage,
    sortConfig,
    handleSort,
    isLoading,
  } = props;

  const [editingStock, setEditingStock] = useState(false);
  const [getEditdata, setGetEditData] = useState<WatchlistData>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<WatchlistData>();
  const [portfolioData, setPortfolioData] = useState<WatchlistData>();
  const [freeTrialRestrictModal, setFreeTrialRestrictModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const user = getLocalStorageUser();
  const token = user.token;
  const [searchInput, setSearchInput] = useState(searchQuery);

  const updateSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery(value);
      setCurrentPage(1);
    }, 600),
  ).current;

  const portfolioCount = Number(user?.portfolio_count ?? 0);

  const stockLimit = Number(process.env.NEXT_PUBLIC_STOCK_COUNT_LIMIT);
  const isLimitReached = user?.is_trial && portfolioCount >= stockLimit;

  useEffect(() => {
    getData();
  }, [currentPage, selectedRange, searchQuery, rowsPerPage, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnClick = async (response: string) => {
    if (response === "Delete") {
      if (!token) {
        alertService.error("Authentication token not found");
        return;
      }
      try {
        await fetchWrapper.delete(
          `/api/users/watchlists/${selectedRow?.id}`,
          undefined,
          { Authorization: `Bearer ${token}` },
        );
        const storedUser = getLocalStorageUser();

        if (storedUser) {
          const updatedUser = {
            ...storedUser,
            watchlist_count: Math.max(
              Number(storedUser.watchlist_count ?? 0) - 1,
              0,
            ),
          };

          localStorage.setItem("BetaUser", JSON.stringify(updatedUser));
        }
        if (records.length > 1) {
          getData();
        } else if (currentPage > 1) {
          const newPage = currentPage - 1;
          params.set("page", newPage.toString());
          setCurrentPage(newPage);
          getData();
        } else {
          getData();
        }
        setConfirmationModal(false);
      } catch (error) {
        alertService.error("Failed to delete portfolio item");
        console.error("Delete error:", error);
      }
    } else {
      setConfirmationModal(false);
    }
  };

  const getTableBody = () => {
    if (isLoading) {
      return <WatchlistSkeleton />;
    } else if (records.length === 0) {
      return (
        <tr>
          <td className="py-3 px-6 text-center" colSpan={10}>
            <div className="flex justify-center items-center py-30">
              <span className="ml-2 text-gray-500 dark:text-gray-200">
                No records found
              </span>
            </div>
          </td>
        </tr>
      );
    } else {
      return (
        <>
          {records.map((row) => {
            const changeValue = Number(row.ltp) - row.close;
            const changePercent = (changeValue / row.close) * 100;
            const isNoChange = changeValue === 0;
            const isPositiveChange = changeValue > 0;

            return (
              <tr
                key={row.id}
                className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {/* STOCK */}
                <td className="py-3 px-6 whitespace-nowrap text-start">
                  <span
                    style={{ backgroundColor: getSectorColor(row.sector) }}
                    className={`w-3 h-3 rounded-full`}
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {row.symbol}
                    </div>
                    <div className="text-xs text-gray-500">{row.sector}</div>
                  </div>
                </td>

                {/* ADDED ON DATE */}
                <td className="py-3 px-6 text-center dark:text-gray-200">
                  {getFormattedDate(row.record_date.toString())}
                </td>

                {/* CURRENT PRICE */}
                <td className="py-3 px-6 text-center dark:text-gray-200">
                  <div className="flex flex-col items-center gap-0.5">
                    <FlashingValue
                      value={formatCurrencyWithLocalString(row.ltp)}
                      isPositive={
                        row.ltp && row.close && row.ltp - row.close === 0
                          ? null
                          : !(row.ltp && row.close && row.ltp - row.close < 0)
                      }
                      flashTrigger={row.lastUpdated}
                      className="text-sm font-medium"
                    />
                    <div className="text-xs text-gray-500">{`Vol. ${(row.volume / 1000000).toFixed(2)}M`}</div>
                  </div>
                </td>

                {/* ALERT PRICE */}
                <td className="py-3 px-6 text-center dark:text-gray-200">
                  <div className="flex items-center justify-around gap-6">
                    {!row.above_price && !row.below_price ? (
                      "-"
                    ) : (
                      <>
                        {row.above_price && (
                          <div className="flex flex-col items-end">
                            <span className="font-medium">
                              {formatCurrencyWithLocalString(row.above_price)}
                            </span>
                            <span className="text-xs text-[#00A85F] dark:text-green-500">
                              Above
                            </span>
                          </div>
                        )}
                        {row.below_price && (
                          <div className="flex flex-col items-end">
                            <span className="font-medium">
                              {formatCurrencyWithLocalString(row.below_price)}
                            </span>
                            <span className="text-xs text-red-500">Below</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </td>

                {/* CHANGE */}
                <td
                  className={`py-3 px-6 text-center font-medium ${
                    isNoChange
                      ? "text-gray-900 dark:text-white"
                      : isPositiveChange
                        ? "text-[#00A85F] dark:text-green-500"
                        : "text-red-500"
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    {!isNoChange &&
                      (isPositiveChange ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-trending-up"
                        >
                          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                          <polyline points="16 7 22 7 22 13"></polyline>
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-trending-down"
                        >
                          <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline>
                          <polyline points="16 17 22 17 22 11"></polyline>
                        </svg>
                      ))}
                    {formatPercentage(Math.abs(changePercent || 0))}
                  </div>
                  <div className="text-xs">
                    {isNoChange ? "" : isPositiveChange ? "+" : "-"}
                    {formatCurrencyWithLocalString(Math.abs(changeValue) || 0)}
                  </div>
                </td>

                {/* STATUS */}
                <td className="py-3 px-6 text-center">
                  {!row.above_price && !row.below_price ? (
                    "-"
                  ) : (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        row.status === "Active"
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {row.status === "Active" ? "Active" : "Triggered"}
                    </span>
                  )}
                </td>

                {/* FREQUENCY */}
                <td className="py-3 px-6 text-center">
                  {row.is_recurring === null ? (
                    "-"
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {row.is_recurring ? "everytime" : "once"}
                    </span>
                  )}
                </td>

                {/* NOTES */}
                <td className="py-3 px-6 text-gray-600 dark:text-gray-300 align-middle text-center">
                  {row.notes ? (
                    <div className="relative group max-w-[250px] text-center break-words cursor-pointer">
                      {row.notes.length > 100
                        ? `${row.notes.substring(0, 97)}...`
                        : row.notes}

                      <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-800 text-white dark:bg-gray-900 dark:text-gray-100 p-3 rounded-md shadow-lg z-50 w-[300px] break-words whitespace-normal text-left">
                        {row.notes}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center w-full">-</div>
                  )}
                </td>

                {/* ACTIONS */}
                <td className="py-3 px-6 text-center">
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPortfolioData(row);
                        if (isLimitReached && !row?.added_to_portfolio) {
                          setFreeTrialRestrictModal(true);
                          return;
                        }
                        setIsModalOpen(true);
                      }}
                      title={
                        isLimitReached && !row?.added_to_portfolio
                          ? "Free trial limit reached"
                          : "Add to Portfolio"
                      }
                      className={`rounded-full p-1 transition-all duration-200 ${
                        isLimitReached && !row?.added_to_portfolio
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <div className="relative w-5 h-5 flex items-center justify-center">
                        <Image
                          src={
                            row?.added_to_portfolio
                              ? BriefCaseFilledIcon
                              : Briefcase
                          }
                          alt={
                            row?.added_to_portfolio
                              ? "In Portfolio"
                              : "Add to Portfolio"
                          }
                          style={{
                            filter: row?.added_to_portfolio
                              ? "invert(62%) sepia(66%) saturate(1200%) hue-rotate(350deg) brightness(100%) contrast(95%)"
                              : "",
                          }}
                          className={`object-contain transition-all duration-200 brightness-60 hover:brightness-30 hover:dark:brightness-70
                              ${row?.added_to_portfolio ? "filter-orange" : "filter invert dark:invert-0 brightness-30 dark:brightness-70"}`}
                        />
                      </div>
                    </button>

                    <button className="hover:text-blue-500 text-gray-400 cursor-pointer">
                      <Edit2
                        size={16}
                        onClick={() => {
                          setEditingStock(true);
                          setGetEditData(row);
                        }}
                      />
                    </button>
                    <button className="hover:text-red-500 text-gray-400 cursor-pointer">
                      <Trash2
                        size={16}
                        onClick={() => {
                          setSelectedRow(row);
                          setConfirmationModal(true);
                        }}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </>
      );
    }
  };

  return (
    <div className="rounded-md border border-[#d0d4e4] dark:border-[#394564] bg-white dark:bg-[#121419] shadow-[5px_1px_5px_rgba(0,0,0,0.09)] h-[calc(100vh-264px)]">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center px-2 py-3 w-full">
        <h2 className="text-lg font-semibold dark:text-white px-4 w-full md:w-auto mb-2 md:mb-0">
          Watchlist Stocks
        </h2>
        <div className="flex gap-2 w-full md:w-auto sm:flex-row items-start sm:items-center px-4 space-y-2 sm:space-y-0 sm:space-x-3 max-[1024px]:mb-4 max-[1024px]:mx-auto">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-end items-center space-x-2">
            <div className="relative w-full mr-0 sm:w-auto">
              <input
                type="text"
                placeholder="Search stocks..."
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  updateSearchQuery(value);
                }}
                className="cursor-pointer bg-white dark:bg-[#1E1E2D] border border-gray-600 rounded-md pl-9 pr-3 py-1.5 text-sm text-[#3D3D3D] dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 font-figtree w-full sm:w-auto sm:min-w-40"
              />
              <Search className="cursor-pointer absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
            </div>

            <select
              value={selectedRange}
              onChange={(e) => {
                setSelectedRange(e.target.value);
                setCurrentPage(1);
              }}
              className="cursor-pointer bg-white dark:bg-[#1E1E2D] border border-gray-600 rounded-md px-3 py-1.5 text-sm text-[#3D3D3D] dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 font-figtree w-full sm:w-auto sm:min-w-24"
            >
              <option value="all">All Stocks</option>
              <option value="active">Active Alerts</option>
              <option value="triggered">Triggered Alerts</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="transition-all duration-300 overflow-auto h-[calc(100%-115px)] flex flex-col">
        <table className="w-full text-sm relative">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#f4f5f8] dark:bg-[#30324e] border-b border-[#d0d4e4] dark:border-[#394564]">
              <SortableHeader
                label="Stock"
                sortKey="symbol"
                className="text-left"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableHeader
                label="Added on date"
                sortKey="created_at"
                className="text-center"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableHeader
                label="Current Price"
                sortKey="ltp"
                className="text-center"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <th className="text-center py-3 px-6 font-medium normal-case text-[#3D3D3D] dark:text-white">
                Alert Price
              </th>
              <SortableHeader
                label="Today's Change"
                sortKey="change"
                className="text-center"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableHeader
                label="Alert"
                sortKey="status"
                className="text-center"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              {/* <th className="text-center py-3 px-6 font-medium normal-case text-[#3D3D3D] dark:text-white">
                Volatility
              </th> */}
              <SortableHeader
                label="Frequency"
                sortKey="is_recurring"
                className="text-center"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <th className="py-3 px-6 text-center font-medium normal-case text-[#3D3D3D] dark:text-white w-[200px]">
                Notes
              </th>
              <th className="py-3 px-6 text-center font-medium normal-case text-[#3D3D3D] dark:text-white">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {getTableBody()}
          </tbody>
        </table>
      </div>
      {records.length > 0 && (
        <div className="">
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            onRowsChange={setRowsPerPage}
          />
        </div>
      )}
      {confirmationModal && (
        <ConfirmationModal
          title={"Remove Stock from WatchList"}
          message={"Are you sure you want to remove stock from WatchList?"}
          show={confirmationModal}
          setShow={setConfirmationModal}
          isDeleteModal={true}
          handleOnClick={handleOnClick}
        />
      )}
      {isModalOpen && portfolioData && (
        <PortfolioCard
          selectedStock={portfolioData}
          closeModal={closeModal}
          openModal={isModalOpen}
          getData={getData}
        />
      )}

      {editingStock && getEditdata && (
        <EditPageElement
          getEditdata={getEditdata}
          setEditingStock={setEditingStock}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          getData={getData}
          isWatchlist={true}
          showDate={false}
        />
      )}

      <FreeTrialRestrictionModal
        isOpen={freeTrialRestrictModal}
        onClose={() => setFreeTrialRestrictModal(false)}
        variant="addStock"
      />
    </div>
  );
};

export default WatchlistTable;
